import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { formatDateRangeLabel, addMonthsIso, CALENDAR_MONTHS, type CalendarMap } from '@/lib/calendar';
import { buildCheckinSet, checkoutOptions } from '@/lib/calendarRules';
import {
  dateRangeReducer,
  initialDateRangeState,
  type DateRangeState,
} from '@/lib/dateRangeState';
import { useShouldPresentAsSheet } from '@/lib/useMediaQuery';
import { useFocusTrap } from '@/lib/useFocusTrap';
import { useAnchoredPosition } from '@/lib/useAnchoredPosition';
import { toApiDate, nightsBetween } from '@/lib/booking';
import { Button } from '@/components/ui/button';
import { Portal } from '@/components/ui/portal';
import { m } from '@/paraglide/messages';

// react-day-picker (+ date-fns, which only DateRangeCalendar imports) is
// heavy enough to blow the entry-chunk budget if it is pulled in eagerly —
// this component is imported statically by the home route (the search bar's
// `presentation="field"` trigger), so a top-level `import` here hoists the
// whole library into the chunk that ships on every landing, not just the
// apartment page (OH-64 whole-branch review, CRITICAL 1). Lazy-loading it
// HERE, rather than only where each presentation is used, fixes both call
// sites in one place: the field/mobile-sheet panel already only mounts this
// while `open` is true, and inline mode gets its own async chunk boundary
// for free. The trigger button itself (the part visible before any click)
// stays in the eager bundle, so the home hero never jumps.
const DateRangeCalendar = lazy(() =>
  import('@/components/DateRangeCalendar').then((mod) => ({ default: mod.DateRangeCalendar })),
);

// Sized to roughly match one month grid (nav + weekday row + up to 6 day
// rows at the library's own --rdp-day-height: 52px) so swapping in the real
// calendar once the chunk loads does not visibly jump the panel height.
function CalendarSkeleton() {
  return (
    <div
      className="h-[380px] w-full animate-pulse rounded-[var(--radius-control)] bg-[var(--surface-sunken)]"
      aria-hidden="true"
    />
  );
}

// The owner's threshold for "wide enough to hold two months" (OH-64). The
// field popover's own measured content width is ~566px (600px panel minus
// its p-4 padding) — this only clears it by 6px, so the two-month CSS
// (`.rdp-host--two-up` in index.css) narrows the day cell specifically in
// two-month mode to fit two side-by-side month grids inside that budget
// instead of `.rdp-months` (flex-wrap) stacking the second month below the
// first. See DateRangeCalendar.tsx and index.css for that half of the fix.
const TWO_MONTH_MIN_WIDTH = 560;

export interface DateRangeValue {
  checkin?: string;
  checkout?: string;
}

export interface DateRangePickerProps {
  value: DateRangeValue;
  onChange: (next: DateRangeValue) => void;
  // 'inline' — calendar in the page flow (apartment card, desktop).
  // 'field'  — trigger button + popover (home search bar, desktop).
  // On a narrow OR short viewport (useShouldPresentAsSheet, OH-131) BOTH
  // present as a bottom sheet instead: one gesture on both surfaces.
  presentation: 'inline' | 'field';
  today?: string;
  dayData?: CalendarMap;
}

export function DateRangePicker({
  value,
  onChange,
  presentation,
  today = toApiDate(new Date()),
  dayData,
}: DateRangePickerProps) {
  const [state, dispatch] = useReducer(dateRangeReducer, initialDateRangeState);
  const [open, setOpen] = useState(false);
  // Narrow OR short (OH-131) — a separate question from "is this a phone",
  // see useShouldPresentAsSheet's own comment. Named for what it decides,
  // not for the viewport shape that most commonly triggers it.
  const presentAsSheet = useShouldPresentAsSheet();
  const [months, setMonths] = useState<1 | 2>(1);

  // The month the calendar opens on. Seeded from whatever the PARENT hands
  // us (URL params, a shared link) and re-seeded only when the parent pushes
  // something we did not send it — the same condition the hydration effect
  // below already uses. An internal day click must never move the view: with
  // two months on screen, picking a check-out in the right-hand month would
  // otherwise scroll the whole grid a month forward under the guest's cursor.
  const [seedCheckin, setSeedCheckin] = useState<string | undefined>(value.checkin);

  // Hydrate from the controlled value (URL params, parent state) without
  // fighting it: only when the parent's value actually differs from ours.
  // Deliberately NOT depending on state.checkin/state.checkout: a fully
  // wired parent echoes back exactly what onChange just sent, so this stays
  // a no-op on the render right after a click — but if it were re-run by an
  // internal-state change alone, it would re-diff against a `value` the
  // parent hasn't updated yet, dispatch 'set' back to the STALE prop, and
  // silently revert the click that just happened.
  useEffect(() => {
    if (value.checkin === state.checkin && value.checkout === state.checkout) return;
    dispatch({ type: 'set', checkin: value.checkin, checkout: value.checkout });
    setSeedCheckin(value.checkin);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.checkin, value.checkout]);

  // Two months when the container can hold them. Container width, not window
  // width: the same component sits in a narrow sidebar and in a wide sheet.
  // jsdom has no ResizeObserver at all (not just a zero width), so this is a
  // straight no-op in tests and `months` stays at its default of 1.
  //
  // A CALLBACK ref, not `useRef` + `useEffect(..., [])`: the measured div
  // only exists in the DOM while the panel is open — for `presentation="field"`
  // and the mobile sheet it lives inside `{open ? (...) : null}` — so a
  // once-only effect with an empty dependency array would run at mount, find
  // `containerRef.current` still null (the panel starts closed), bail out,
  // and never run again on any later `open` toggle. That left the popover
  // and the sheet with no observer EVER attached, `months` stuck at 1
  // forever regardless of real width (OH-64 Task 12). A callback ref fires
  // on every mount AND unmount of the node it's attached to, so opening the
  // panel re-attaches a fresh observer and closing it tears the old one
  // down — exactly the re-measure-per-open behaviour this needs. The inline
  // apartment-card presentation, whose container mounts unconditionally
  // with the component, was never affected by this bug.
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const setContainerRef = useCallback((el: HTMLDivElement | null) => {
    resizeObserverRef.current?.disconnect();
    resizeObserverRef.current = null;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      setMonths(entry.contentRect.width >= TWO_MONTH_MIN_WIDTH ? 2 : 1);
    });
    ro.observe(el);
    resizeObserverRef.current = ro;
  }, []);

  // Clamped into rdp's own navigable window: `startMonth` is today and
  // `endMonth` is today + CALENDAR_MONTHS. react-day-picker's own
  // getInitialMonth already clamps an out-of-range value to that same
  // window internally, so this duplicates rather than replaces that
  // behaviour — done here so the value we HAND DOWN is the one we intend,
  // not one we're merely trusting a third-party clamp (which could change
  // across a version bump) to produce.
  const defaultMonth = useMemo(() => {
    if (!seedCheckin) return undefined;
    if (seedCheckin < today) return today;
    const last = addMonthsIso(today, CALENDAR_MONTHS);
    return seedCheckin > last ? last : seedCheckin;
  }, [seedCheckin, today]);

  const checkinSet = useMemo(
    () => (dayData ? buildCheckinSet(dayData, today) : undefined),
    [dayData, today],
  );

  const options = useMemo(
    () =>
      state.checkin
        ? checkoutOptions(dayData, state.checkin)
        : { selectable: undefined, minNights: 0 },
    [dayData, state.checkin],
  );

  // The single predicate pair. Everything downstream — the calendar's
  // `disabled`, the reducer's action — reads these two functions and nothing
  // else, so there is exactly one place where "can this date be picked" lives.
  //
  // canCheckinAt is NOT just "is this date a valid standalone check-in ever":
  // while a check-in is already pending (checkin-picked), a LATER date that
  // fails as a checkout (min-stay, contiguity, ...) must stay disabled to
  // teach the constraint, even though it would be a perfectly fine check-in
  // on its own economics — that's the whole point of the min-stay notice. An
  // EARLIER date, though, is an unambiguous "change my mind about arrival"
  // gesture and stays clickable, mirroring the restart branch
  // dateRangeReducer already exercises (dateRangeState.test.ts: "restarts
  // from an earlier date clicked after a check-in"). Outside that phase (idle
  // or a completed range) any economically-valid check-in is fair game.
  const canCheckinAt = (iso: string): boolean => {
    const eligible = checkinSet ? checkinSet.has(iso) : iso >= today;
    if (!eligible) return false;
    if (state.phase === 'checkin-picked' && state.checkin) {
      // `<=`, not `<` (OH-141): the day the guest just chose as the arrival
      // must not render disabled. It cannot be a check-out (a stay needs a
      // night), so with a strict `<` it satisfied neither role and the cell
      // greyed itself out the moment it was picked — the selected day looking
      // unavailable. Clicking it again simply re-picks the same arrival.
      return iso <= state.checkin;
    }
    return true;
  };

  const canCheckoutAt = (iso: string): boolean => {
    if (state.phase !== 'checkin-picked' || !state.checkin) return false;
    return options.selectable ? options.selectable.has(iso) : iso > state.checkin;
  };

  function handleDayClick(iso: string) {
    const action = {
      type: 'day-click' as const,
      date: iso,
      canCheckin: canCheckinAt(iso),
      canCheckout: canCheckoutAt(iso),
    };
    // Derive once to decide whether anything changed, then apply that same
    // action — the reducer is pure, so the dispatched result is identical.
    // This is a preview, not a second application: dispatch below is the
    // only place state actually changes, so the click lands exactly once.
    const next: DateRangeState = dateRangeReducer(state, action);
    if (next === state) return;
    // Re-picking the same arrival is a no-op, not a navigation: the reducer
    // returns a fresh object either way, so identity alone cannot tell.
    if (next.checkin === state.checkin && next.checkout === state.checkout) return;
    dispatch(action);
    onChange({ checkin: next.checkin, checkout: next.checkout });
    // Restore focus to the trigger, matching Escape and outside-click below
    // (both already do this). Pre-existing gap, but the portal made the
    // consequence worse: this panel used to sit next to the trigger in the
    // DOM, so a removed node left the browser's next-Tab starting point
    // right there anyway. Portaled to the end of `document.body`, losing
    // focus here moves that starting point to document end instead, so the
    // next Tab lands past everything rather than on the Search button.
    if (next.phase === 'complete') {
      setOpen(false);
      triggerRef.current?.focus();
    }
  }

  // new Date(iso) parses a YYYY-MM-DD string as UTC (spec behaviour for
  // date-only ISO strings) — unlike DateRangeCalendar's parseIso, this is
  // safe here because BOTH checkin and checkout go through the identical
  // conversion and only their millisecond difference is used. Nothing here
  // reads a calendar field (getDate()/getMonth()) off the result, which is
  // the local/UTC bug this codebase already fixed once (see calendar.ts).
  const nights =
    state.checkin && state.checkout
      ? nightsBetween(new Date(state.checkin), new Date(state.checkout))
      : 0;

  // Overlay a11y: Escape closes, an outside click closes (OH-64 Task 12 —
  // everywhere else on the web a popover does this; its absence read as a
  // stuck panel), and focus returns to the trigger either way. The panel
  // announces itself as a dialog. The calendar grid's own keyboard handling
  // (arrows, Home/End) comes from DayPicker and must not be intercepted here.
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return;
      setOpen(false);
      triggerRef.current?.focus();
    }
    // `mousedown`, not `click`: fires before any default focus shift the
    // browser would apply to whatever was clicked, so the explicit
    // `.focus()` below is the one that sticks — the same outcome Escape
    // already gives. A click on the TRIGGER itself is deliberately left
    // alone here (`triggerRef.current?.contains(target)`): the trigger's
    // own onClick toggles `open`, and closing it here first would just
    // make that toggle re-open it. A click anywhere INSIDE the panel
    // (`panelRef`) is also left alone: day clicks, month navigation, and
    // the mobile sheet's own Done button already manage `open` themselves.
    // For the mobile sheet this same listener also closes on a click on
    // the darkened scrim for free — the scrim is outside `panelRef`, so no
    // separate scrim-specific handler is needed.
    function onPointerDown(e: MouseEvent) {
      const target = e.target;
      if (!(target instanceof Node)) return;
      if (panelRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      // A real browser's own default action for mousedown runs AFTER this
      // listener and would blur whatever we focus below — landing on the
      // clicked element if it's focusable, or on <body> otherwise (this is
      // exactly what an unguarded version of this handler does: the panel
      // closes, but focus lands on <body>, not the trigger).
      // preventDefault() cancels only that default focus shift; the
      // subsequent `click` event (and the clicked element's own handler,
      // if any) still fires normally.
      e.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    }
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [open]);

  // A real focus trap, not just "start focus inside the panel": OH-64 lists
  // it among the acceptance criteria, and aria-modal="true" below would be a
  // false promise to assistive tech without one — Tab could otherwise walk a
  // keyboard user straight out of the popover/sheet into the rest of the page.
  useFocusTrap(panelRef, open);

  // Only the desktop popover needs a computed position — the sheet is
  // pinned to the viewport edge by its own CSS, and measuring it would just
  // be wasted work (and an extra ResizeObserver) on every open.
  const anchorPosition = useAnchoredPosition(triggerRef, panelRef, open && !presentAsSheet);

  // True once the panel is actually visible: immediately for the sheet (it
  // has no measured position and is never rendered `visibility: hidden`),
  // only once `useAnchoredPosition` has produced coordinates for the
  // desktop popover (it renders `visibility: hidden` until then — see that
  // hook's own comment and OH-130 spec §3.1).
  const measured = presentAsSheet || anchorPosition !== null;

  // Move focus into the panel so the next Tab lands inside it, not past it.
  // A SEPARATE effect from the listener-registration one above, gated on
  // `measured` rather than folded into `[open]`: the popover commits TWICE
  // per open — once hidden (React's first pass, before `useAnchoredPosition`'s
  // own `useLayoutEffect` has measured it), once visible (the synchronous
  // re-render that measurement triggers) — and React flushes a commit's
  // pending passive effects BEFORE the layout-effect-triggered re-render, so
  // an effect keyed on `[open]` alone fires during the FIRST (hidden) commit.
  // `.focus()` on a `visibility: hidden` element is a no-op in every browser,
  // which is exactly why the popover never actually took focus despite
  // `aria-modal="true"` promising one. Gating on `measured` instead defers
  // the call to the render where the panel's own mutation has already
  // flipped it visible. `measured` (not `anchorPosition` itself) is the
  // dependency on purpose: `anchorPosition` also changes on every scroll/
  // resize recompute (see useAnchoredPosition.ts), which would otherwise
  // re-run this effect and yank focus off a day cell the guest had
  // arrow-keyed to — `measured` flips false→true exactly once per open.
  //
  // preventScroll: the browser's default "scroll the focused element into
  // view" walks up to the nearest scrollable ancestor, and on the home
  // page that is the hero section (`overflow-hidden`, so scrollable even
  // though it shows no bar). It scrolled the hero 369px and the page
  // 513px, leaving the guest looking at the section BELOW the fold with
  // the search button gone — see the OH-130 spec §1 D1. The portal stops
  // the panel from being clipped; this stops the scroll. Both are needed.
  useEffect(() => {
    if (!open || !measured) return;
    panelRef.current?.focus({ preventScroll: true });
  }, [open, measured]);

  const calendar = (
    <div ref={setContainerRef} className="w-full">
      <Suspense fallback={<CalendarSkeleton />}>
        <DateRangeCalendar
          checkin={state.checkin}
          checkout={state.checkout}
          today={today}
          months={presentAsSheet ? 1 : months}
          dayData={dayData}
          canCheckin={canCheckinAt}
          canCheckout={canCheckoutAt}
          onDayClick={handleDayClick}
          defaultMonth={defaultMonth}
        />
      </Suspense>
      <div className="mt-2 flex items-center justify-between gap-3">
        <p className="text-sm text-muted">
          {nights >= 2
            ? m.picker_nights_selected({ nights })
            : nights === 1
              ? m.picker_one_night_selected()
              : state.phase === 'checkin-picked' && options.minNights > 1
                ? m.picker_min_nights({ nights: options.minNights })
                : m.picker_prompt()}
        </p>
        {state.checkin ? (
          <button
            type="button"
            className="text-sm font-semibold text-[var(--ollio-navy)] hover:underline"
            onClick={() => {
              dispatch({ type: 'clear' });
              onChange({ checkin: undefined, checkout: undefined });
            }}
          >
            {m.picker_clear()}
          </button>
        ) : null}
      </div>
    </div>
  );

  // Human-readable, not the raw ISO pair (OH-64): "5 Aug – 6 Aug" rather than
  // "2026-08-05 – 2026-08-06", which was both a machine format and too wide
  // for the hero search bar's flex-1 field. `today` — already threaded
  // through this component for the exact same real-clock-avoidance reason —
  // decides whether the year needs to show at all.
  const label =
    state.checkin && state.checkout
      ? formatDateRangeLabel(state.checkin, state.checkout, today)
      : m.picker_dates_placeholder();

  const trigger = (
    <button
      ref={triggerRef}
      type="button"
      onClick={() => setOpen((o) => !o)}
      aria-expanded={open}
      aria-haspopup="dialog"
      className="w-full rounded-2xl px-4 py-2 text-left sm:rounded-full"
    >
      <span className="block text-[10px] font-bold uppercase tracking-[0.08em] text-[var(--ollio-gold-deep)]">
        {m.picker_dates_label()}
      </span>
      {/* truncate (whitespace-nowrap + overflow-hidden + text-ellipsis): the
          label must not wrap the trigger onto a second line and grow it
          taller than the "Where"/"Guests" fields beside it (OH-64) — an
          overlong label (a straddling-year range) clips with an ellipsis
          instead. This alone only stops the wrap; a flex-item ancestor also
          needs `min-w-0` to actually let this shrink and clip rather than
          push the row wider (see the "field" consumer, HomeSearchBar in
          routes/index.tsx). */}
      <span className="mt-0.5 block truncate text-sm font-semibold text-[var(--text-body)]">
        {label}
      </span>
    </button>
  );

  // Narrow OR short: one gesture on both surfaces — tap the field, get a
  // bottom sheet.
  if (presentAsSheet) {
    return (
      <div>
        {trigger}
        {open ? (
          <Portal>
            <div className="fixed inset-0 z-[1000] flex flex-col justify-end bg-[rgba(15,23,42,0.55)]">
              <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-label={m.picker_dates_label()}
                tabIndex={-1}
                // dvh, not vh: on iOS Safari `vh` measures the LARGEST
                // viewport, so an 85vh sheet extends under the collapsing
                // browser chrome and can put the sticky Done button out of
                // reach. Not reproducible on the Chrome/macOS harness this
                // was found on — fixed on the owner's call (OH-130 D6).
                className="max-h-[85dvh] overflow-y-auto rounded-t-[var(--radius-2xl)] bg-card p-4"
              >
                {calendar}
                <Button
                  variant="primary"
                  size="lg"
                  className="sticky bottom-0 mt-3 w-full"
                  onClick={() => {
                    // Restore focus to the trigger, matching Escape/outside
                    // click and the completed-range close above.
                    setOpen(false);
                    triggerRef.current?.focus();
                  }}
                >
                  {m.picker_done()}
                </Button>
              </div>
            </div>
          </Portal>
        ) : null}
      </div>
    );
  }

  // The popover and the sheet live inside `{open ? … : null}` and remount on
  // every open, so they re-read defaultMonth for free. The inline
  // presentation (the apartment sidebar) mounts once and stays, so it needs
  // an explicit remount — keyed on the SEED, not on the selection, so a day
  // click never triggers it.
  if (presentation === 'inline') return <div key={seedCheckin ?? 'none'}>{calendar}</div>;

  return (
    // `relative` is vestigial: the portaled panel below is `position:
    // fixed` (via useAnchoredPosition), not `absolute`, so nothing here
    // actually anchors it — harmless only as long as that stays true.
    <div className="relative">
      {trigger}
      {open ? (
        <Portal>
          <div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-label={m.picker_dates_label()}
            tabIndex={-1}
            // Fixed + measured coordinates instead of `absolute top-full`:
            // every host of this panel turned out to clip it, capture it, or
            // stick it out of reach (OH-130 §2). Width stays exactly as it
            // was — the two-month layout clears TWO_MONTH_MIN_WIDTH by 6px,
            // so a collision must move the panel, never narrow it.
            style={{
              position: 'fixed',
              top: anchorPosition?.top ?? 0,
              left: anchorPosition?.left ?? 0,
              visibility: anchorPosition ? 'visible' : 'hidden',
            }}
            className="z-[1000] w-[600px] max-w-[90vw] rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card p-4 shadow-[var(--shadow-lg)]"
          >
            {calendar}
          </div>
        </Portal>
      ) : null}
    </div>
  );
}
